"use client";

import { useState, useCallback } from "react";
import { useToast } from "./Toast";
import { MarkdownBlock } from "@/components/web/markdown";
import { artifactUrl } from "@/lib/web-client";
import ResultView from "@/components/web/ResultView";
import type { TaskResult as AppTaskResult } from "@/lib/app-types";

const MNR = "var(--font-manrope), 'Manrope', sans-serif";
const URB = "var(--font-urbanist), sans-serif";
const PJK = "'Plus Jakarta Sans', var(--font-plus-jakarta), sans-serif";

// Suppress unused variable warning for URB (kept for future use)
void URB;

interface Competitor {
  name: string;
  description: string;
}

interface AttachedFile {
  name: string;
  icon: "file-pdf" | "file-xlsx";
}

interface ResultCardProps {
  title?: string;
  summary?: string;
  competitors?: Competitor[];
  recommendations: string;
  files: AttachedFile[];
  variant?: "full" | "inline";
  onApprove?: () => void;
  onRevise?: () => void;
  /** Real task ID — used for artifact download URLs. */
  taskId?: string | null;
  /** Full result object (from app-types). Contains format + webResult when real. */
  result?: AppTaskResult | null;
}

// ── Clipboard + copy button (fallback path; rich path uses ResultView's) ──
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to execCommand
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.cssText = "position:fixed;top:0;left:0;opacity:0;pointer-events:none";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

function CopyTextButton({ content }: { content: string }) {
  const [copied, setCopied] = useState(false);
  const onClick = useCallback(() => {
    copyText(content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }, [content]);
  return (
    <button
      onClick={onClick}
      style={{
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: 7,
        padding: "5px 12px",
        color: "#CDC2D8",
        fontSize: 12,
        fontWeight: 500,
        cursor: "pointer",
        fontFamily: "var(--font-plus-jakarta), sans-serif",
      }}
    >
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

// ── Download helpers ──────────────────────────────────────────────

function downloadBlob(content: string, mimeType: string, filename: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const date = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = filename.replace("{{date}}", date);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function getDownloadProps(result: AppTaskResult | null | undefined, title?: string, summary?: string, recommendations?: string): {
  label: string;
  mimeType: string;
  filename: string;
  content: string;
} {
  const fmt = result?.format;
  const content = result?.rawContent ?? [title, summary, recommendations].filter(Boolean).join("\n\n");
  const date = "{{date}}";

  // Honor the user's explicitly chosen download format first (md/txt/doc/…).
  const dlKey = result?.downloadFormat?.toLowerCase();
  if (dlKey === "txt") {
    return { label: "Download .txt", mimeType: "text/plain; charset=utf-8", filename: `result-${date}.txt`, content };
  }
  if (dlKey === "doc" || dlKey === "docx") {
    const body = content.split("\n").map((l) => `<p>${l.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;") || "&nbsp;"}</p>`).join("");
    const doc = "<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'><head><meta charset='utf-8'></head><body>" + body + "</body></html>";
    return { label: "Download .doc", mimeType: "application/msword", filename: `result-${date}.doc`, content: doc };
  }
  if (dlKey === "md") {
    return { label: "Download .md", mimeType: "text/markdown; charset=utf-8", filename: `result-${date}.md`, content };
  }

  if (fmt === "html" || (content && (content.trimStart().startsWith("<!DOCTYPE") || content.trimStart().startsWith("<html")))) {
    return { label: "Download .html", mimeType: "text/html", filename: `result-${date}.html`, content };
  }
  if (fmt === "code") {
    // Try to infer extension from artifacts
    const ext = result?.files?.[0]?.name?.split(".").pop() ?? "txt";
    const extMap: Record<string, string> = { ts: "ts", tsx: "tsx", js: "js", jsx: "jsx", py: "py", rs: "rs", go: "go", html: "html", css: "css" };
    const resolvedExt = extMap[ext] ?? ext;
    return { label: `Download .${resolvedExt}`, mimeType: "text/plain", filename: `result-${date}.${resolvedExt}`, content };
  }
  if (fmt === "csv") {
    return { label: "Download .csv", mimeType: "text/csv", filename: `result-${date}.csv`, content };
  }
  // Default: markdown
  return { label: "Download .md", mimeType: "text/markdown; charset=utf-8", filename: `result-${date}.md`, content };
}

export default function ResultCard({
  title,
  summary,
  competitors,
  recommendations,
  files,
  variant = "full",
  onApprove,
  onRevise,
  taskId,
  result,
}: ResultCardProps) {
  const isInline = variant === "inline";
  const toast = useToast();

  const handleFileClick = (file: AttachedFile) => {
    if (taskId) {
      window.open(artifactUrl(taskId, file.name), "_blank", "noopener");
    } else {
      toast.show("Downloads available in production mode. This is a demo.");
    }
  };

  const handleDownload = () => {
    // Prefer real artifact files if present
    if (taskId && result?.webResult?.artifacts && result.webResult.artifacts.length > 0) {
      const art = result.webResult.artifacts[0];
      const url = artifactUrl(taskId, art.filename);
      const a = document.createElement("a");
      a.href = url;
      a.download = art.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }

    const { mimeType, filename, content } = getDownloadProps(result, title, summary, recommendations);
    downloadBlob(content, mimeType, filename);
  };

  const downloadProps = getDownloadProps(result, title, summary, recommendations);
  const downloadBtnLabel = downloadProps.label;
  // Display filename for the synthesized attachment pill (date placeholder resolved),
  // using the user's selected format extension — matches the top Download button.
  const attachmentName = downloadProps.filename.replace("{{date}}", new Date().toISOString().slice(0, 10));

  // ── Rich result path ──────────────────────────────────────────────
  // When a real webResult is attached (non-demo tasks), delegate body to ResultView.
  const webResult = result?.webResult ?? null;
  const hasRichResult = Boolean(webResult?.preview);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, alignItems: "flex-start", width: "100%" }}>
      {/* Title */}
      {!isInline && title && (
        <h2
          style={{
            fontFamily: MNR,
            fontWeight: 600,
            fontSize: 20,
            lineHeight: "28px",
            color: "white",
            letterSpacing: "-0.5px",
            margin: 0,
          }}
        >
          {title}
        </h2>
      )}

      {/* Rich result body — HTML iframe / code block / markdown / csv via ResultView */}
      {hasRichResult && webResult ? (
        <div style={{ width: "100%" }}>
          <ResultView result={webResult} downloadFormat={result?.downloadFormat} />
        </div>
      ) : (
        <>
          {/* SUMMARY — only shown for text/md results (no format or format=md/text) */}
          {!isInline && summary && (
            <Section label="SUMMARY">
              <div style={{ display: "flex", marginBottom: 8 }}>
                <CopyTextButton content={result?.rawContent ?? summary} />
              </div>
              <MarkdownBlock content={summary} />
            </Section>
          )}

          {/* KEY COMPETITORS */}
          {!isInline && competitors && competitors.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-start", width: "100%" }}>
              <SectionHeading>KEY COMPETITORS</SectionHeading>
              <div style={{ display: "flex", flexDirection: "column", gap: 11.7, width: "100%" }}>
                {competitors.map((comp, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      gap: 12,
                      alignItems: "flex-start",
                      width: "100%",
                      animation: `appFadeInUp 400ms ease-out ${i * 100}ms both`,
                    }}
                  >
                    <span
                      style={{
                        fontFamily: MNR,
                        fontWeight: 600,
                        fontSize: 15,
                        lineHeight: "24.38px",
                        color: "#4DE082",
                        flexShrink: 0,
                      }}
                    >
                      {i + 1}.
                    </span>
                    <p style={{ fontFamily: MNR, fontSize: 14, lineHeight: "24.38px", color: "#F2F0ED", margin: 0 }}>
                      <span style={{ fontWeight: 600 }}>{comp.name}</span>
                      <span style={{ fontWeight: 400 }}> — {comp.description}</span>
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* RECOMMENDATIONS — hidden when empty */}
          {recommendations && (
            <Section label="RECOMMENDATIONS">
              <MarkdownBlock content={recommendations} />
            </Section>
          )}
        </>
      )}

      {/* ATTACHED FILES + DOWNLOAD */}
      <div
        style={{
          borderTop: "1px solid rgba(255,255,255,0.05)",
          paddingTop: 17,
          display: "flex",
          flexDirection: "column",
          gap: 12,
          width: "100%",
        }}
      >
        <p
          style={{
            fontFamily: MNR,
            fontWeight: 600,
            fontSize: 10,
            lineHeight: "16.25px",
            color: "#CDC2D8",
            textTransform: "uppercase",
            margin: 0,
          }}
        >
          ATTACHED FILES
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "flex-start" }}>
          {files.map((file, i) => (
            <div
              key={i}
              className="app-file-pill"
              onClick={() => handleFileClick(file)}
              role="button"
              style={{
                background: "rgba(242,240,237,0.05)",
                border: "1px solid rgba(255,255,255,0.05)",
                borderRadius: 32,
                padding: "9px 13px",
                display: "flex",
                gap: 8,
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <img
                src={`/app-icons/${file.icon}.svg`}
                alt=""
                style={{ width: file.icon === "file-pdf" ? 11.667 : 10.5, height: file.icon === "file-pdf" ? 11.667 : 10.5 }}
              />
              <span
                style={{
                  fontFamily: MNR,
                  fontWeight: 500,
                  fontSize: 12,
                  lineHeight: "16px",
                  color: "#F2F0ED",
                }}
              >
                {file.name}
              </span>
            </div>
          ))}

          {/* Synthesized attachment for rich text results (no real artifacts):
              present the deliverable as a file in the user's selected format,
              matching the top Download button's extension/MIME/content. */}
          {hasRichResult && files.length === 0 && (
            <div
              className="app-file-pill"
              onClick={handleDownload}
              role="button"
              style={{
                background: "rgba(242,240,237,0.05)",
                border: "1px solid rgba(255,255,255,0.05)",
                borderRadius: 32,
                padding: "9px 13px",
                display: "flex",
                gap: 8,
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="M6 1v7M3 5l3 3 3-3M2 10h8" stroke="#8635F6" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span style={{ fontFamily: MNR, fontWeight: 500, fontSize: 12, lineHeight: "16px", color: "#F2F0ED" }}>
                {attachmentName}
              </span>
            </div>
          )}

          {/* Download button — only when ResultView isn't already providing one
              (rich result path owns its own format-matched download + copy). */}
          {!hasRichResult && (
            <button
              onClick={handleDownload}
              style={{
                background: "rgba(134,53,246,0.10)",
                border: "1px solid rgba(134,53,246,0.3)",
                borderRadius: 32,
                padding: "9px 13px",
                display: "flex",
                gap: 8,
                alignItems: "center",
                cursor: "pointer",
                fontFamily: MNR,
                fontWeight: 500,
                fontSize: 12,
                lineHeight: "16px",
                color: "#CDC2D8",
              }}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="M6 1v7M3 5l3 3 3-3M2 10h8" stroke="#8635F6" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {downloadBtnLabel}
            </button>
          )}
        </div>
      </div>

      {/* Action buttons */}
      {!isInline && (
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
          <button
            onClick={onApprove}
            className="app-btn-success"
            style={{
              background: "#4ADE80",
              height: 40,
              padding: "0 20px",
              borderRadius: 9999,
              border: "none",
              cursor: "pointer",
              fontFamily: PJK,
              fontWeight: 700,
              fontSize: 12,
              lineHeight: "16px",
              color: "#171717",
              boxShadow: "0px 10px 15px -3px rgba(74,222,128,0.2), 0px 4px 6px -4px rgba(74,222,128,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Approve & pay
          </button>
          <button
            onClick={onRevise}
            className="app-btn-gray"
            style={{
              background: "#2D2C2C",
              height: 40,
              padding: "12px 16px",
              borderRadius: 9999,
              border: "none",
              cursor: "pointer",
              fontFamily: PJK,
              fontWeight: 700,
              fontSize: 12,
              lineHeight: "16px",
              color: "#E5E2E1",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Request revision
          </button>
        </div>
      )}
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <p
      style={{
        fontFamily: MNR,
        fontWeight: 600,
        fontSize: 14,
        letterSpacing: "1.4px",
        lineHeight: "20px",
        color: "#D5BAFF",
        textTransform: "uppercase",
        margin: 0,
      }}
    >
      {children}
    </p>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 7.19, alignItems: "flex-start", width: "100%" }}>
      <SectionHeading>{label}</SectionHeading>
      {children}
    </div>
  );
}
